import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { FieldArray, Formik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { Collapse } from "react-bootstrap";
import ReactDOMServer from "react-dom/server";
import styled from "styled-components";
import {
  AppDatePicker,
  AppFileUploader,
  AppInput,
  AppInputNumber,
  AppModal,
  AppNoInfo,
  AppRemixIcon,
  AppSingleSelect,
} from "@/layout/AppComponents";
import { toFormData } from "../../../minions/forms";
import ModuleComsaSideBarReports from "./ModuleComsaSideBarReports";
import { renderCommaFloat } from "../../../minions/int";
import Observations from "../../../layout/screenSpecifics/Modules/ComsaReports/Observations";
import Translator from "../../../layout/components/helpers/Translator";
import BreadcrumbHandler from "../../../layout/components/breadcrumb/BreadcrumbHandler";
import { axiosPath } from "../../../routing/helpers/router";
import useIsMounted from "../../../layout/components/hooks/useIsMounted";
import _ from "lodash";
import {
    getFile,
  handleDisabled,
  handleMinDate,
} from "../../../layout/screenSpecifics/Modules/ComsaReports/tools/comsaReportsTools";

const ModuleComsaCoolingGasUseReportScreen = () => {
  const { moduleBridgeHex, subModuleBridgeHex, plantHex } = useParams();
  const isMounted = useIsMounted();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [reportList, setReportList] = useState([]);
  const [showSendReportModal, setShowSendReportModal] = useState(false);
  const [isSendingReport, setIsSendingReport] = useState(false);
  const [reportFormInitialValues, setReportFormInitialValues] = useState(null);
  const [months] = useState(
    Array.from({ length: 12 }, (_, i) => ({
      label: <Translator  file="user" name={`calendar.month${i + 1}`} />,
      value: i + 1,
    }))
  );
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteReportId, setDeleteReportId] = useState();
  const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);
  const [reportType] = useState("coolingGasUse");
  const dynamicAreaButton = useRef(null);
  const [filters, setFilters] = useState({
    month: null,
    year: null,
    gasType: "",
    quantityFrom: null,
    quantityTo: null,
  });
  const [filterMonth, setFilterMonth] = useState(null);
  const [filterYear, setFilterYear] = useState(null);
  const [filterGasType, setFilterGasType] = useState("");
  const [filterQuantityFrom, setFilterQuantityFrom] = useState(null);
  const [filterQuantityTo, setFilterQuantityTo] = useState(null);
  const [actualFilters, setActualFilters] = useState({
    month: null,
    year: null,
    gasType: "",
    quantityFrom: null,
    quantityTo: null,
  });
  const [moduleData, setModuleData] = useState(null);

  // > FILTERS
  const handleFilters = () => {
    const newFilters = {
      month: filterMonth,
      year: filterYear,
      gasType: filterGasType,
      quantityFrom: filterQuantityFrom,
      quantityTo: filterQuantityTo,
    };
    if (!_.isEqual(newFilters, actualFilters)) {
      setActualFilters(newFilters);
      setLoading(true);
      setFilters(newFilters);
    }
  };
  const handleRemoveFilters = () => {
    const newFilters = {
      month: null,
      year: null,
      gasType: "",
      quantityFrom: null,
      quantityTo: null,
    };
    setFilterMonth(null);
    setFilterYear(null);
    setFilterGasType("");
    setFilterQuantityFrom(null);
    setFilterQuantityTo(null);
    setActualFilters(newFilters);
    setLoading(true);
    setFilters(newFilters);
  };
  // < FILTERS

  // > LOAD MODULE DATA //
  const loadModuleData = useCallback(async () => {
    try {
      const response = await axios.post(
        axiosPath("api_r_user_module_comsaReports_getModuleData"),
        { moduleBridgeHex, subModuleBridgeHex, plantHex }
      );
      return response.data;
    } catch (err) {}
  }, []);

  useEffect(() => {
    loadModuleData().then((res) => {
      if (isMounted.current) setModuleData(res);
    });
  }, []);
  // > LOAD MODULE DATA //

  // > LOAD REPORTS //
  const loadReports = useCallback(async () => {
    try {
      const response = await axios.post(
        axiosPath("api_r_user_module_comsaReports_report"),
        {
          type: reportType,
          moduleBridgeHex,
          subModuleBridgeHex,
          plantHex,
          filters,
        }
      );
      return response.data;
    } catch (err) {}
  }, [filters]);

  useEffect(() => {
    if (loading) {
      loadReports().then((res) => {
        if (isMounted.current) {
          setReportList(res);
          setIsFiltersPanelOpen(false);
          setLoading(false);
        }
      });
    }
  }, [loading]);
  // < LOAD REPORTS //

  // < ADD REPORT
  const addReport = async (values) => {
    try {
      const response = await axios.post(
        axiosPath("api_r_user_module_comsaReports_addReport"),
        toFormData({
          ...values,
          year: values.year,
          type: reportType,
          moduleBridgeHex,
          subModuleBridgeHex,
          plantHex,
        }),
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return response.data;
    } catch (err) {}
  };

  const handleAddReport = () => {
    setShowSendReportModal(true);
    setReportFormInitialValues({
      id: null,
      month: null,
      year: null,
      dynamicArea: [{ gasType: "", quantity: "" }],
      files: null,
      deletedFiles: [],
    });
  };
  // < ADD REPORT

  // < EDIT REPORT
  const editReport = async (values) => {
    try {
      const response = await axios.post(
        axiosPath("api_r_user_module_comsaReports_editReport"),
        toFormData({
          ...values,
          year: values.year,
          type: reportType,
          moduleBridgeHex,
          subModuleBridgeHex,
          plantHex,
        }),
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return response.data;
    } catch (err) {}
  };

  const handleEditReport = (report) => {
    const editableReport = _.omit(report, ["gasType", "quantity"]);
    setReportFormInitialValues({
      ...editableReport,
      month: Number(report.month),
      year: report.year,
      dynamicArea: [{ gasType: report.gasType, quantity: report.quantity }],
      deletedFiles: [],
    });
    setShowSendReportModal(true);
  };

  const handleDeletedFiles = (setFieldValue, deletedFiles, salt) => {
    if (deletedFiles.includes(salt)) {
      setFieldValue(
        "deletedFiles",
        deletedFiles.filter((s) => s !== salt)
      );
    } else setFieldValue("deletedFiles", [...deletedFiles, salt]);
  };
  // < EDIT REPORT

  // < DELETE REPORT
  const removeReport = useCallback(async () => {
    try {
      const response = await axios.post(
        axiosPath("api_r_user_module_comsaReports_removeReport"),
        {
          id: deleteReportId,
          type: reportType,
          moduleBridgeHex,
          subModuleBridgeHex,
          plantHex,
        }
      );
      return response.data;
    } catch (err) {}
  });

  useEffect(() => {
    if (deleteReportId) setShowDeleteModal(true);
  }, [deleteReportId]);

  const handleOnDeleteModalConfirm = () => {
    removeReport().then((res) => {
      setReportList(reportList.filter((r) => r.id !== res.id));
      setDeleteReportId(null);
      setShowDeleteModal(false);
    });
  };

  const handleOnDeleteModalClose = () => {
    setDeleteReportId(null);
    setShowDeleteModal(false);
  };
  // < DELETE REPORT

  const removeDynamicArea = (dynamicArea, arrayHelpers, i) => {
    if (dynamicArea.length > 1) arrayHelpers.remove(i);
  };

  // < FORM SCHEMA
  const validationSchema = Yup.object().shape({
    month: Yup.number()
      .nullable()
      .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
    year: Yup.date()
      .nullable()
      .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
    dynamicArea: Yup.array().of(
      Yup.object().shape({
        gasType: Yup.string().required(
          ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)
        ),
        quantity: Yup.string()
          .matches(
            /^\d+(\.\d{1,3})?$/,
            ReactDOMServer.renderToString(
              <Translator file="module-comsaReports" name="0.000.format" />
            )
          )
          .required(ReactDOMServer.renderToString(<Translator file="user" name="user.required" />)),
      })
    ),
  });
  // < FORM SCHEMA

  // < SAVE FORM
  const handleSaveReport = (values) => {
    setIsSendingReport(true);
    const reportFormFunction = values.id
      ? editReport(values)
      : addReport(values);
    reportFormFunction
      .then(() => {
        if (isMounted.current) {
          setShowSendReportModal(false);
          setLoading(true);
        }
      })
      .finally(() => {
        if (isMounted.current) setIsSendingReport(false);
      });
  };
  // < SAVE FORM

  if (!moduleData) return <AppNoInfo />;

  return (
    <div className="pl-2">
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: `${
                    moduleData.plantName
                  } - ${ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="evaluation"
                    />
                  )}`,
                  route: {
                    name: "user_module_legalCompliance_evaluation",
                    params: {
                      moduleBridgeHex,
                      plantHex,
                      page: 1,
                      filtersHex: 0,
                    },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator file="module-comsaReports" name="indicators" />
                  ),
                  route: {
                    name: "user_module_comsaReports_home",
                    params: { moduleBridgeHex, subModuleBridgeHex, plantHex },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaReports"
                      name="cooling.gas.use"
                    />
                  ),
                },
              ]
            : [{ name: "..." }]
        }
      />
      <section className="content">
        <ModuleComsaSideBarReports
          activeRoute="user_module_comsaReports_cooling_gas_use"
          moduleData={moduleData}
        />
        <div className="row">
          <div className="col-12">
            <Title>
              <Translator file="module-comsaReports" name="cooling.gas.use" />
            </Title>
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <span
              className="btn btn-success btn-sm mr-2"
              onClick={() => handleAddReport()}
              title={ReactDOMServer.renderToString(
                <Translator
                  file="module-comsaReports"
                  name="new.cooling.gas.use"
                />
              )}
            >
              <AppRemixIcon icon="add" />
            </span>
            <span
              className={`btn btn-sm btn-${
                isFiltersPanelOpen ? "" : "outline-"
              }info float-right`}
              onClick={() => setIsFiltersPanelOpen(!isFiltersPanelOpen)}
            >
              <Translator file="module-comsaReports" name="filters" />
            </span>
            {(filters.month ||
              filters.year ||
              filters.gasType ||
              filters.quantityFrom ||
              filters.quantityTo) && (
              <span
                className="btn btn-danger btn-sm mr-1 float-right"
                onClick={() => handleRemoveFilters()}
              >
                <AppRemixIcon icon="delete-bin-6" />
              </span>
            )}
          </div>
        </div>
        <Observations reportType={reportType} />
        <div className="row mt-3">
          <div className="col-12">
            <Collapse in={isFiltersPanelOpen}>
              <div className="card mb-0">
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <AppDatePicker
                        title={
                          <Translator file="module-comsaReports" name="year" />
                        }
                        value={filterYear}
                        onChange={(e) => setFilterYear(e)}
                        minDate={new Date(2021, 0, 1)}
                        dateFormat="yyyy"
                        displayDateFormat="YYYY"
                        placeholder={
                          <Translator
                            file="module-comsaReports"
                            name="select.a.year"
                          />
                        }
                        isClearable
                        showYearPicker
                      />
                    </div>
                    <div className="col-md-6">
                      <AppSingleSelect
                        options={months}
                        onChange={(e) => setFilterMonth(e)}
                        title={
                          <Translator file="module-saicaReports" name="month" />
                        }
                        icon={<AppRemixIcon icon="calendar" />}
                        placeholder={
                          <Translator
                            file="module-comsaReports"
                            name="select.a.month"
                          />
                        }
                        value={filterMonth}
                      />
                    </div>
                    <div className="col-md-6">
                      <AppInputNumber
                        title={`${ReactDOMServer.renderToString(
                          <Translator
                            file="module-comsaReports"
                            name="quantity"
                          />
                        )} (${ReactDOMServer.renderToString(
                          <Translator file="module-comsaReports" name="from" />
                        )})`}
                        icon={<AppRemixIcon icon="hashtag" noVariants />}
                        value={filterQuantityFrom || ""}
                        onChange={(number) => setFilterQuantityFrom(number)}
                        decimals={3}
                      />
                    </div>
                    <div className="col-md-6">
                      <AppInputNumber
                        title={`${ReactDOMServer.renderToString(
                          <Translator
                            file="module-comsaReports"
                            name="quantity"
                          />
                        )} (${ReactDOMServer.renderToString(
                          <Translator file="module-comsaReports" name="to" />
                        )})`}
                        icon={<AppRemixIcon icon="hashtag" noVariants />}
                        value={filterQuantityTo || ""}
                        onChange={(number) => setFilterQuantityTo(number)}
                        decimals={3}
                      />
                    </div>
                    <div className="col-md-6">
                      <AppInput
                        title={
                          <Translator file="module-comsaReports" name="type" />
                        }
                        icon={<AppRemixIcon icon="airplay" />}
                        onChange={(v) => setFilterGasType(v)}
                        value={filterGasType}
                        isClearable
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-12">
                      <div className="float-right">
                        <span
                          className="btn btn-info btn-sm"
                          onClick={() => handleFilters()}
                        >
                          <Translator
                            file="module-saicaReports"
                            name="filter"
                          />
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Collapse>
          </div>
        </div>
        <div className="row mt-3">
          <div className="col-12">
            <div className="card sr-header">
              <div className="card-body">
                {loading ? (
                  <div>
                    <Translator file="module-saicaReports" name="loading" />
                  </div>
                ) : (
                  <>
                    {reportList.length > 0 ? (
                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            <th>
                              <Translator
                                file="module-comsaReports"
                                name="year"
                              />
                            </th>
                            <th>
                              <Translator
                                file="module-comsaReports"
                                name="month"
                              />
                            </th>
                            <th>
                              <Translator
                                file="module-comsaReports"
                                name="type"
                              />
                            </th>
                            <th>
                              <Translator
                                file="module-comsaReports"
                                name="quantity"
                              />{" "}
                              (Kg)
                            </th>
                            <th>
                              <Translator
                                file="module-comsaReports"
                                name="evidence"
                              />
                            </th>
                            <th style={{ minWidth: "113px", width: "113px" }} />
                          </tr>
                        </thead>
                        <tbody>
                          {reportList &&
                            reportList.map((r) => (
                              <tr key={r.id}>
                                <td>{r.year}</td>
                                <td>
                                  <Translator
                                    file="user"
                                    name={`calendar.month${r.month}`}
                                  />
                                </td>
                                <td>{r.gasType}</td>
                                <td>{renderCommaFloat(r.quantity)}</td>
                                <td>
                                  {r.files &&
                                    Object.keys(r.files).map((salt, index) => (
                                        <span
                                            key={index}
                                            className="mr-1 pointer"
                                            title={r.files[salt].name}
                                            onClick={() => getFile(moduleBridgeHex, subModuleBridgeHex, plantHex, reportType, r.id, salt, dispatch)}
                                        >
                                            <AppRemixIcon icon="file-text" />
                                        </span>
                                    ))}
                                </td>
                                <td className="text-center">
                                  <button
                                    type="button"
                                    className="btn btn-info btn-xs mr-1"
                                    onClick={() => handleEditReport(r)}
                                    title={ReactDOMServer.renderToString(
                                      <Translator
                                        file="module-comsaReports"
                                        name="edit"
                                      />
                                    )}
                                    disabled={handleDisabled(
                                      moduleData,
                                      r?.year
                                    )}
                                  >
                                    <AppRemixIcon icon="edit" />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-danger btn-xs"
                                    onClick={() => setDeleteReportId(r.id)}
                                    title={ReactDOMServer.renderToString(
                                      <Translator
                                        file="module-comsaReports"
                                        name="delete"
                                      />
                                    )}
                                    disabled={handleDisabled(
                                      moduleData,
                                      r?.year
                                    )}
                                  >
                                    <AppRemixIcon icon="delete-bin-6" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="alert alert-info">
                        <Translator
                          file="module-saicaReports"
                          name="no.results"
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <AppModal
          show={showSendReportModal}
          title={ReactDOMServer.renderToString(
            <Translator file="module-comsaReports" name="report.form" />
          )}
          size="lg"
          onClose={() => setShowSendReportModal(false)}
        >
          <Formik
            validateOnChange={false}
            validateOnBlur={false}
            enableReinitialize
            initialValues={reportFormInitialValues}
            validationSchema={validationSchema}
            onSubmit={handleSaveReport}
          >
            {({ setFieldValue, handleSubmit, values, errors }) => (
              <>
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <AppDatePicker
                        title={
                          <Translator file="module-comsaReports" name="year" />
                        }
                        value={values.year}
                        onChange={(opt) => setFieldValue("year", opt)}
                        minDate={handleMinDate(moduleData)}
                        dateFormat="yyyy"
                        displayDateFormat="YYYY"
                        placeholder={
                          <Translator
                            file="module-comsaReports"
                            name="select.a.year"
                          />
                        }
                        isClearable
                        showYearPicker
                        popperClassName="react-datepicker-popper react-datepicker-popper-1000"
                      />
                      {errors.year && (
                        <div className="text-danger">*{errors.year}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <AppSingleSelect
                        options={months}
                        onChange={(opt) => setFieldValue("month", opt)}
                        title={
                          <Translator file="module-saicaReports" name="month" />
                        }
                        icon={<AppRemixIcon icon="calendar" />}
                        placeholder={
                          <Translator
                            file="module-comsaReports"
                            name="select.a.month"
                          />
                        }
                        value={values.month}
                      />
                      {errors.month && (
                        <div className="text-danger">*{errors.month}</div>
                      )}
                    </div>
                  </div>
                  <div className="col-sm-12">
                    <FieldArray
                      name="dynamicArea"
                      render={(arrayHelpers) => (
                        <>
                          {values.dynamicArea.map((tv, i) => (
                            <div
                              key={i}
                              className="row pt-2 pb-2"
                              style={{ borderBottom: "1px solid #f4f6f9" }}
                            >
                              <div className="col-lg-12">
                                <button
                                  className="btn btn-danger btn-xs float-right"
                                  onClick={() =>
                                    removeDynamicArea(
                                      values.dynamicArea,
                                      arrayHelpers,
                                      i
                                    )
                                  }
                                  disabled={values.dynamicArea.length === 1}
                                  title={ReactDOMServer.renderToString(
                                    <Translator
                                      file="module-comsaReports"
                                      name="delete"
                                    />
                                  )}
                                >
                                  <AppRemixIcon icon="delete-bin-6" />
                                </button>
                              </div>
                              <div className="col-md-6">
                                <div className="mb-3">
                                  <AppInput
                                    title={
                                      <Translator
                                        file="module-comsaReports"
                                        name="type"
                                      />
                                    }
                                    icon={<AppRemixIcon icon="airplay" />}
                                    onChange={(v) =>
                                      setFieldValue(
                                        `dynamicArea[${i}].gasType`,
                                        v
                                      )
                                    }
                                    value={values.dynamicArea[i].gasType}
                                    isClearable
                                  />
                                  {errors?.dynamicArea &&
                                    errors.dynamicArea[i]?.gasType && (
                                      <div className="text-danger">
                                        *{errors.dynamicArea[i].gasType}
                                      </div>
                                    )}
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="mb-3">
                                  <AppInputNumber
                                    title={
                                      <Translator
                                        file="module-comsaReports"
                                        name="quantity"
                                      />
                                    }
                                    icon={
                                      <AppRemixIcon icon="hashtag" noVariants />
                                    }
                                    value={values.dynamicArea[i].quantity}
                                    onChange={(value) =>
                                      setFieldValue(
                                        `dynamicArea[${i}].quantity`,
                                        value
                                      )
                                    }
                                    decimals={3}
                                    append={
                                      <div className="input-group-append">
                                        <span className="input-group-text">
                                          Kg
                                        </span>
                                      </div>
                                    }
                                  />
                                  {errors?.dynamicArea &&
                                    errors.dynamicArea[i]?.quantity && (
                                      <div className="text-danger">
                                        *{errors.dynamicArea[i].quantity}
                                      </div>
                                    )}
                                </div>
                              </div>
                            </div>
                          ))}
                          {!values.id && (
                            <div className="row mt-3">
                              <div className="col-sm-12">
                                <span
                                  className="pointer"
                                  onClick={() =>
                                    arrayHelpers.push({
                                      gasType: "",
                                      quantity: "",
                                    })
                                  }
                                  ref={dynamicAreaButton}
                                >
                                  <AppRemixIcon icon="add-box" />{" "}
                                  <Translator
                                    file="module-comsaReports"
                                    name="add.types.and.quantities"
                                  />
                                </span>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    />
                  </div>
                  <div className="col-sm-12 mt-3 mb-3">
                    <label>
                      <Translator
                        file="module-comsaReports"
                        name="attach.evidence"
                      />
                    </label>
                    <AppFileUploader
                      isMulti
                      placeholder={
                        !values.files || Object.keys(values?.files).length === 0
                          ? ReactDOMServer.renderToString(
                              <Translator
                                file="module-legalCompliance"
                                name="select.one.multiple.files"
                              />
                            )
                          : ReactDOMServer.renderToString(
                              <Translator
                                file="general"
                                name="select.number.of.elements"
                                variables={{
                                  elements: Object.keys(values?.files).length,
                                }}
                              />
                            )
                      }
                      onChange={(opt) => setFieldValue("files", opt)}
                      btnText={<Translator file="general" name="search..." />}
                    />
                  </div>
                </div>
                {values.id &&
                  Object.keys(reportList.find((r) => r.id === values.id).files)
                    .length > 0 && (
                    <div className="row">
                      <div className="col-12">
                        <label>
                          <Translator file="general" name="general.delete" />
                        </label>
                        <div>
                          {Object.keys(values.files).map((salt, index) => (
                            <div key={index}>
                              <label className="mb-2 pointer font-weight-light">
                                <input
                                  type="checkbox"
                                  className="mr-1"
                                  value={salt}
                                  onChange={() =>
                                    handleDeletedFiles(
                                      setFieldValue,
                                      values.deletedFiles,
                                      salt
                                    )
                                  }
                                  checked={
                                    values.deletedFiles &&
                                    values.deletedFiles[salt]
                                  }
                                />
                                {values.files[salt].name}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                <div className="row">
                  <div className="col-12">
                    <button
                      className="btn btn-success btn-block"
                      type="submit"
                      onClick={handleSubmit}
                      disabled={isSendingReport}
                    >
                      <Translator
                        file="module-comsaReports"
                        name={values.id ? "edit" : "add"}
                      />
                    </button>
                  </div>
                </div>
              </>
            )}
          </Formik>
        </AppModal>
        <AppModal
          show={showDeleteModal}
          type="danger"
          isConfirmBtn
          isCancelBtn
          confirmBtnText={
            <Translator file="module-comsaReports" name="delete" />
          }
          title={<Translator file="module-comsaReports" name="delete.report" />}
          onClose={handleOnDeleteModalClose}
          onConfirm={handleOnDeleteModalConfirm}
        >
          <Translator file="module-comsaReports" name="confirm.delete.report" />
        </AppModal>
      </section>
    </div>
  );
};

export default ModuleComsaCoolingGasUseReportScreen;

const Title = styled.h1`
  text-align: center;
  font-size: 30px;
`;
